import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { deleteRequest, downloadBlobGet, fetchTextGet, getRequest } from '../../api/ApiController';
import { DatabaseContext } from '../../redux/DatabaseStore';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import '../../assets/css/style.css';
import '../../assets/css/iconsmenu.css';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Avatar } from 'primereact/avatar';
import { PanelMenu } from 'primereact/panelmenu';
import { MdSearch, MdSearchOff } from 'react-icons/md';
import AuthContext from '../../components/AuthContext';
import UtilContext from '../../components/UtilContext';
import { LocaleContext } from '../../components/LocaleContext';
import { OverflowMenuProvider } from '../../components/toolbar/OverflowMenu';
import IntersectionObserverWrapper from '../../components/toolbar/IntersectionObserverWrap';
import {
  AddIcon,
  EditIcon,
  CopyIcon,
  DeleteIcon,
  PrintIcon,
  SearchIcon,
  ExportIcon,
  RefreshIcon,
  VerticalLineIcon,
  BackToFirstIcon,
  BackIcon,
  ForwardIcon,
  ForwardToLastIcon,
  FavoriteIcon,
  PriceIcon,
  EditRowIcon,
} from '../svg/svgIcons';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { DateTime } from 'luxon';
import { scrollToFirst as scrollToFirstUtil, scrollUp as scrollUpUtil, scrollDown as scrollDownUtil, scrollToLast as scrollToLastUtil } from '../../utils/tabulatorScroll';
import { dateUserHeaderFilter } from '../../utils/dateUserHeaderFilter';
import { inputOnEnterHeaderFilter } from '../../utils/inputOnEnterHeaderFilter';
import {
  TABULATOR_LIST_ID_MIN_WIDTH,
  tabulatorColumnMinWidthFromTitle,
  tabulatorDateUserColumnMinWidth,
} from '../../utils/tabulatorColumnMinWidth';
import { getEffectiveMask } from '../../utils/effectiveMask';
import { applyUserMaskRowToTabulator, cleanSorters, safeParse, sanitizeTabulatorLayoutAttributes } from '../../utils/tabulatorProgressiveLoading';
import { getTabulatorPersistLayoutEvents } from '../../utils/tabulatorPersistLayout';
import { getTabulatorLayoutForStandardMask } from '../../utils/tabulatorLayoutUtils';
import PrintOptionsDialog from '../../components/PrintOptionsDialog';
import { buildExportQueryStringFromParams, openPrintHtmlWindow, triggerBlobDownload } from '../../utils/serviceTaskExport';
import { EXPORT_DOCUMENT_TITLE_KEYS, xlsxDownloadFilename } from '../../utils/exportExcelNaming';
import TabulatorMaskDialog from '../../utils/tabulatorMaskDialog';
import { applyTabulatorRuntimeI18n } from '../../utils/tabulatorRuntimeI18n';


const DEFAULT_COLUMNS_VISIBLE_ROLE = Object.freeze({
  id: true,
  name: true,
  description: true,
  created_at: true,
  updated_at: true,
});

const ROLE_MASK_FIELD_I18N = Object.freeze({
  id: 'RoleList.Column.Id',
  name: 'RoleList.Column.Name',
  description: 'RoleList.Column.Description',
  created_at: 'RoleList.Column.CreatedAt',
  updated_at: 'RoleList.Column.UpdatedAt',
});

const ROLE_COLUMN_I18N_KEYS = ROLE_MASK_FIELD_I18N;

function mergeRoleVisibility(saved) {
  const merged = { ...DEFAULT_COLUMNS_VISIBLE_ROLE };
  if (saved && typeof saved === 'object') {
    Object.keys(DEFAULT_COLUMNS_VISIBLE_ROLE).forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(saved, k)) merged[k] = saved[k] !== false;
    });
  }
  if (!Object.values(merged).some(Boolean)) return { ...DEFAULT_COLUMNS_VISIBLE_ROLE };
  return merged;
}

function normalizeRoleColumnVisibilityForSave(stateObj) {
  const out = {};
  for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_ROLE)) out[field] = stateObj?.[field] !== false;
  return out;
}

function columnsFromTabulatorForStandardMaskSaveRole(raw) {
  const out = {};
  for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_ROLE)) out[field] = raw?.[field] !== false;
  return out;
}

function clearHeaderFilterForField(table, field) {
  if (!table || field == null) return;
  try { table.setHeaderFilterValue?.(field, '', true); } catch (_) {}
}

function applyDataColumnVisibilityToTableRole(table, visibilityMap) {
  if (!table) return;
  for (const f of Object.keys(DEFAULT_COLUMNS_VISIBLE_ROLE)) {
    if (visibilityMap[f] === false) {
      clearHeaderFilterForField(table, f);
      table.hideColumn?.(f);
    } else {
      table.showColumn?.(f);
    }
  }
  setTimeout(() => table.redraw?.(true), 0);
}

function countVisibleDataColumnsRole(vis) {
  if (!vis || typeof vis !== 'object') return 0;
  return Object.keys(DEFAULT_COLUMNS_VISIBLE_ROLE).filter((k) => vis[k] !== false).length;
}

const normalizeText = (value) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

function textHeaderFilter(headerValue, rowValue) {
  if (!headerValue) return true;
  return normalizeText(rowValue).includes(normalizeText(headerValue));
}

export default function RoleList() {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { t, locale } = useContext(LocaleContext);
  const {
    toast,
    setUserMaskFilter,
    setStandardMaskFilter,
    openFavoriteDialog,
    setFastView,
    setDialogText,
    setVisible,
    openChosenNotificationPreferences,
    getDateFormat,
    formatDate,
    getUserInitials,
    getUserColor,
  } = useContext(UtilContext);

  const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFmt = `${dateFmt} HH:mm`;

  const insertIcon = !user?.permissions?.includes('add users');
  const [editIcon, setEditIcon] = useState(true);
  const [deleteIcon, setDeleteIcon] = useState(true);
  const [copyIcon, setCopyIcon] = useState(true);
  const [searchIconColor, setSearchIconColor] = useState('');
  const [visibleSearch, setVisibleSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [columnsVisible, setColumnsVisible] = useState({ ...DEFAULT_COLUMNS_VISIBLE_ROLE });
  const [maskVisible, setMaskVisible] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState({});
  const [printChoiceVisible, setPrintChoiceVisible] = useState(false);
  const [printOrientationChoice, setPrintOrientationChoice] = useState('portrait');
  const [printLayoutChoice, setPrintLayoutChoice] = useState('branded');

  const ref = useRef(null);
  const overlayRef = useRef(null);
  const isRestoringRef = useRef(false);
  const submittedSearchValueRef = useRef('');

  const getTable = useCallback(() => ref.current?.table ?? ref.current, []);

  const canSetStandardMask = user?.permissions?.includes('set standard mask');
  const hasApplicableStandardMask = useMemo(() => {
    const row = (state.standard_mask_filters || []).find((m) => m.page === 'RoleList');
    if (!row) return false;
    const attributes = sanitizeTabulatorLayoutAttributes(safeParse(row.attributes, []));
    if (!Array.isArray(attributes) || attributes.length <= 1) return false;
    const cols = safeParse(row.columns, {});
    const merged = mergeRoleVisibility(cols && typeof cols === 'object' ? cols : {});
    return Object.values(merged).some(Boolean);
  }, [state.standard_mask_filters]);

  const refreshList = useCallback((showDialog = false) => {
    const table = getTable();
    if (showDialog) {
      table?.alert?.(t('Common.LoadingData'));
    }
    getRequest('/api/roles')
      .then((response) => {
        const data = response?.data ?? [];
        dispatch({ type: 'load_roles', newRoles: data });
      })
      .catch(() => {})
      .finally(() => {
        if (showDialog) table?.clearAlert?.();
      });
  }, [dispatch, getTable, t]);

  const idHeaderFilter = useCallback((headerValue, rowValue, rowData) => {
    if (!headerValue) return true;
    return String(rowData?.id ?? '').includes(String(headerValue).trim());
  }, []);

  const dateOrUserFilter = useCallback((headerValue, rowValue, rowData, filterParams) => {
    if (!headerValue) return true;
    const raw = String(headerValue || '').trim();
    if (raw.startsWith('range:')) {
      const p = raw.slice('range:'.length).split(':');
      if (p.length >= 2 && p[0] && p[1]) {
        const from = DateTime.fromFormat(p[0], 'yyyy-MM-dd').startOf('day');
        const to = DateTime.fromFormat(p[1], 'yyyy-MM-dd').endOf('day');
        const dt = DateTime.fromISO(String(rowData?.[filterParams?.name] || ''));
        return dt.isValid ? dt >= from && dt <= to : false;
      }
      return true;
    }
    const search = raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const name = filterParams?.name;
    const userObj = name === 'created_at' ? rowData?.created_by_user : name === 'updated_at' ? rowData?.last_touched_by_user : null;
    const uname = userObj?.employee?.name || userObj?.username || '';
    const dt = DateTime.fromISO(String(rowData?.[filterParams?.name] || ''));
    const dateStr = dt.isValid ? `${dt.toFormat(dateFmt)} ${dt.toFormat(dateTimeFmt)} ${dt.toFormat('yyyy-MM-dd')}` : '';
    const hay = `${uname} ${dateStr}`.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    return hay.includes(search);
  }, [dateFmt, dateTimeFmt]);

  function onEditRow(roleRow) {
    dispatch({
      type: 'create_new_tab', name: t('RoleList.Tab.Edit'), translationKey: 'RoleList.Tab.Edit', legacyLabel: t('RoleList.Tab.Edit'),
      page: 'RoleAdd',
      props: { role: roleRow, type: 'edit' },
      mask: 'gset012',
    });
  }

  function EditRowFormatter(props) {
    const rowData = props.cell.getRow().getData();
    return (
      <div className="flex items-center justify-center w-full h-full">
        <button type="button" onClick={() => onEditRow(rowData)}>
          <EditRowIcon />
        </button>
      </div>
    );
  }

  function TruncateSpan({ text }) {
    const str = (text ?? '').toString();
    return <span className="block w-full min-w-0 truncate whitespace-nowrap" title={str || undefined}>{str}</span>;
  }
  function TruncateCellFormatter(props) {
    return <TruncateSpan text={props.cell.getValue()} />;
  }

  function CreatedAtUserFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const u = rowData?.created_by_user;
    const raw = rowData?.created_at;
    const text = raw
      ? (formatDate ? formatDate(raw, dateTimeFmt, true) : DateTime.fromISO(String(raw)).toFormat(dateTimeFmt))
      : '';
    const displayName = u?.employee?.name || u?.username || '';
    return (
      <div className="flex items-center justify-start w-full h-full min-w-0 gap-1">
        {u ? (
          <Avatar
            className="shrink-0"
            title={displayName}
            label={getUserInitials?.(displayName) || ''}
            shape="circle"
            style={{ backgroundColor: getUserColor?.(u?.username) || '#6b7280', color: '#ffffff' }}
          />
        ) : null}
        <div className="min-w-0 flex-1 truncate whitespace-nowrap" title={text || undefined}>{text}</div>
      </div>
    );
  }

  function UpdatedAtUserFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const u = rowData?.last_touched_by_user;
    const raw = rowData?.updated_at;
    const text = raw
      ? (formatDate ? formatDate(raw, dateTimeFmt, true) : DateTime.fromISO(String(raw)).toFormat(dateTimeFmt))
      : '';
    const displayName = u?.employee?.name || u?.username || '';
    return (
      <div className="flex items-center justify-start w-full h-full min-w-0 gap-1">
        {u ? (
          <Avatar
            className="shrink-0"
            title={displayName}
            label={getUserInitials?.(displayName) || ''}
            shape="circle"
            style={{ backgroundColor: getUserColor?.(u?.username) || '#6b7280', color: '#ffffff' }}
          />
        ) : null}
        <div className="min-w-0 flex-1 truncate whitespace-nowrap" title={text || undefined}>{text}</div>
      </div>
    );
  }

  const columns = useMemo(() => {
    const colTitle = (key) => t(key);
    return [
      { field: 'rowSelection', formatter: 'rowSelection', titleFormatter: 'rowSelection', hozAlign: 'center', width: 44, headerSort: false, resizable: false, frozen: true, print: false, download: false },
      { title: '', field: 'editor', hozAlign: 'center', width: 44, headerSort: false, formatter: reactFormatter(<EditRowFormatter />), resizable: false, frozen: true, print: false, download: false },
      { title: colTitle('RoleList.Column.Id'), field: 'id', minWidth: TABULATOR_LIST_ID_MIN_WIDTH, widthGrow: 1, hozAlign: 'left', sorter: 'number', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: idHeaderFilter },
      { title: colTitle('RoleList.Column.Name'), field: 'name', hozAlign: 'left', minWidth: tabulatorColumnMinWidthFromTitle(colTitle('RoleList.Column.Name')), widthGrow: 1, formatter: reactFormatter(<TruncateCellFormatter />), headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter },
      { title: colTitle('RoleList.Column.Description'), field: 'description', hozAlign: 'left', minWidth: tabulatorColumnMinWidthFromTitle(colTitle('RoleList.Column.Description')), widthGrow: 1, formatter: reactFormatter(<TruncateCellFormatter />), headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter },
      { title: colTitle('RoleList.Column.CreatedAt'), field: 'created_at', hozAlign: 'left', minWidth: tabulatorDateUserColumnMinWidth(colTitle('RoleList.Column.CreatedAt')), widthGrow: 1, formatter: reactFormatter(<CreatedAtUserFormatter />), headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: dateOrUserFilter, headerFilterFuncParams: { name: 'created_at' }, headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt }, sorter: 'datetime', sorterParams: { format: 'iso' } },
      { title: colTitle('RoleList.Column.UpdatedAt'), field: 'updated_at', hozAlign: 'left', minWidth: tabulatorDateUserColumnMinWidth(colTitle('RoleList.Column.UpdatedAt')), widthGrow: 1, formatter: reactFormatter(<UpdatedAtUserFormatter />), headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: dateOrUserFilter, headerFilterFuncParams: { name: 'updated_at' }, headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt }, sorter: 'datetime', sorterParams: { format: 'iso' } },
    ];
  }, [dateFmt, dateTimeFmt, dateOrUserFilter, formatDate, getUserColor, getUserInitials, idHeaderFilter, t]);

  useEffect(() => {
    const table = getTable();
    if (!table?.getColumns) return;
    applyTabulatorRuntimeI18n(table, t, {
      columnTitleKeys: ROLE_COLUMN_I18N_KEYS,
      placeholder: t('Common.NoData'),
      reformatRows: true,
    });
  }, [locale, t, getTable]);


  const getMaskColumnLabel = useCallback((field) => {
    const key = ROLE_MASK_FIELD_I18N[field];
    return key ? t(key) : field;
  }, [t]);

  const getPrintColumnsOrder = useCallback(() => {
    const table = getTable();
    const allowed = Object.keys(DEFAULT_COLUMNS_VISIBLE_ROLE);
    const fallback = () => allowed.filter((f) => columnsVisible[f] !== false);
    if (!table?.getColumns) return fallback();
    const allowedSet = new Set(allowed);
    const out = [];
    const seen = new Set();
    try {
      table.getColumns().forEach((col) => {
        const field = col.getField?.() ?? col.getDefinition?.()?.field;
        if (!field || !allowedSet.has(field)) return;
        if (columnsVisible[field] === false) return;
        if (seen.has(field)) return;
        seen.add(field);
        out.push(field);
      });
    } catch (_) {
      return fallback();
    }
    return out.length > 0 ? out : fallback();
  }, [getTable, columnsVisible]);

  const parseHeaderFiltersToParams = useCallback(() => {
    const table = getTable();
    const hf = table?.getHeaderFilters?.() ?? [];
    const out = {};
    hf.forEach((f) => {
      if (!f?.field) return;
      let v = f.value;
      if (v != null && typeof v === 'object' && !Array.isArray(v) && Array.isArray(v.value)) {
        v = v.value;
      }
      if (Array.isArray(v)) {
        if (v.length === 0) return;
        v = v.length === 1 ? v[0] : v.join(' ');
      }
      if (v == null || String(v).trim() === '') return;
      out[`filter_${f.field}`] = v;
    });
    return out;
  }, [getTable]);

  const buildRoleExportQueryString = useCallback((opts = {}) => {
    const table = getTable();
    const params = {
      ...parseHeaderFiltersToParams(),
      toolbar_search: String(submittedSearchValueRef.current ?? '').trim() || undefined,
      sorters: JSON.stringify(cleanSorters(table?.getSorters?.() ?? [])),
    };
    return buildExportQueryStringFromParams(params, opts);
  }, [getTable, parseHeaderFiltersToParams]);

  const runRolePrint = useCallback(async () => {
    try {
      setPrintChoiceVisible(false);
      setDialogText(t('Common.PreparingPrint'));
      setVisible(true);
      const suffix = buildRoleExportQueryString({
        includePrintColumns: true,
        printLayout: printLayoutChoice,
        printOrientation: printOrientationChoice,
        printColumns: getPrintColumnsOrder(),
      });
      const html = await fetchTextGet(`/api/roles/export/print${suffix}`);
      if (!openPrintHtmlWindow(html)) {
        toast?.current?.show({ severity: 'warn', summary: t('DepartmentList.Toast.PrintTitle'), detail: t('Common.EnablePopups'), life: 4000 });
      }
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('DepartmentList.Toast.PrintTitle'), detail: e?.message || t('DepartmentList.Toast.PrintFailed'), life: 4500 });
    } finally {
      setVisible(false);
    }
  }, [buildRoleExportQueryString, getPrintColumnsOrder, printLayoutChoice, printOrientationChoice, setDialogText, setVisible, t, toast]);

  const exportRoleXlsx = useCallback(async () => {
    try {
      setDialogText(t('Common.PreparingExport'));
      setVisible(true);
      const suffix = buildRoleExportQueryString({
        includePrintColumns: true,
        printColumns: getPrintColumnsOrder(),
      });
      const defaultXlsx = xlsxDownloadFilename(t(EXPORT_DOCUMENT_TITLE_KEYS.ROLES));
      const { blob, filename } = await downloadBlobGet(`/api/roles/export/xlsx${suffix}`, defaultXlsx);
      triggerBlobDownload(blob, filename || defaultXlsx);
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('DepartmentList.Toast.ExportTitle'), detail: e?.message || t('DepartmentList.Toast.ExportFailed'), life: 4500 });
    } finally {
      setVisible(false);
    }
  }, [buildRoleExportQueryString, getPrintColumnsOrder, setDialogText, setVisible, t, toast]);

  const closeMenu = () => {
    OverflowMenuProvider.setVisible(null);
    setExpandedKeys({});
  };

  const openMaskDialog = () => {
    setColumnsVisible(mergeRoleVisibility(columnsVisible));
    setMaskVisible(true);
    closeMenu();
  };

  const saveRoleMaskColumns = () => {
    const normalized = normalizeRoleColumnVisibilityForSave(columnsVisible);
    if (!Object.values(normalized).some(Boolean)) {
      toast?.current?.show({ severity: 'warn', summary: t('DepartmentList.Toast.MaskWarnTitle'), detail: t('DepartmentList.Toast.SelectOneColumn'), life: 4000 });
      return;
    }
    const filtersSnapshot = getTable()?.getHeaderFilters?.() ?? [];
    setUserMaskFilter('RoleList', 'all', normalized, filtersSnapshot, null, null);
    setColumnsVisible(normalized);
    setMaskVisible(false);
  };

  const confirmSaveStandardMask = () => {
    confirmDialog({
      message: t('DepartmentList.SaveStandardMaskMessage'),
      header: t('DepartmentList.SaveStandardMaskHeader'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Save'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        const table = getTable();
        const layout = getTabulatorLayoutForStandardMask(table);
        if (!layout) return;
        const columnsForSave = columnsFromTabulatorForStandardMaskSaveRole(layout.columns);
        setStandardMaskFilter('RoleList', columnsForSave, layout.attributes);
      },
    });
  };

  const confirmApplyStandardLayoutToUserMask = () => {
    closeMenu();
    const standard = (state.standard_mask_filters || []).find((m) => m.page === 'RoleList');
    if (!standard) {
      toast?.current?.show({ severity: 'warn', summary: t('DepartmentList.Toast.StandardMaskWarnTitle'), detail: t('DepartmentList.StandardMaskMissing'), life: 4000 });
      return;
    }
    const attributes = sanitizeTabulatorLayoutAttributes(safeParse(standard.attributes, []));
    if (!Array.isArray(attributes) || attributes.length <= 1) return;
    const jsonColumns = safeParse(standard.columns, {});
    const mergedCols = mergeRoleVisibility(jsonColumns && typeof jsonColumns === 'object' ? jsonColumns : {});
    const colsForSave = normalizeRoleColumnVisibilityForSave(mergedCols);
    confirmDialog({
      message: t('DepartmentList.ApplyStandardMaskMessage'),
      header: t('DepartmentList.ApplyStandardMaskHeader'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Apply'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        setUserMaskFilter('RoleList', 'all', colsForSave, null, attributes, null, () => {
          const table = getTable();
          if (!table) return;
          isRestoringRef.current = true;
          try { table.setColumnLayout?.(attributes); } catch (_) {}
          setColumnsVisible(colsForSave);
          applyDataColumnVisibilityToTableRole(table, colsForSave);
          setTimeout(() => { isRestoringRef.current = false; }, 0);
        });
      },
    });
  };

  function setMask(field, value) {
    if (!Object.prototype.hasOwnProperty.call(DEFAULT_COLUMNS_VISIBLE_ROLE, field)) return;
    const table = getTable();
    const next = { ...DEFAULT_COLUMNS_VISIBLE_ROLE, ...columnsVisible, [field]: value };
    if (value === false && countVisibleDataColumnsRole(next) < 1) {
      toast?.current?.show({ severity: 'warn', summary: t('DepartmentList.Toast.MaskWarnTitle'), detail: t('DepartmentList.Toast.AtLeastOneColumn'), life: 3500 });
      return;
    }
    applyDataColumnVisibilityToTableRole(table, next);
    setColumnsVisible(next);
  }

  function maskSelectAll() {
    const next = { ...DEFAULT_COLUMNS_VISIBLE_ROLE };
    Object.keys(next).forEach((k) => { next[k] = true; });
    applyDataColumnVisibilityToTableRole(getTable(), next);
    setColumnsVisible(next);
  }

  function maskDeselectAll() {
    const next = {};
    Object.keys(DEFAULT_COLUMNS_VISIBLE_ROLE).forEach((k) => { next[k] = false; });
    next.id = true;
    applyDataColumnVisibilityToTableRole(getTable(), next);
    setColumnsVisible(next);
  }

  const onSelectRow = (data) => {
    if (!data || data.length === 0) {
      setEditIcon(true);
      setCopyIcon(true);
      setDeleteIcon(true);
    } else {
      if (data.length === 1) {
        if (user?.permissions?.includes('add users')) setCopyIcon(false);
        if (user?.permissions?.includes('edit users')) setEditIcon(false);
      }
      if (user?.permissions?.includes('delete users')) setDeleteIcon(false);
    }
  };

  const deleteSelected = useCallback(() => {
    const selected = getTable()?.getSelectedData?.() ?? [];
    if (selected.length === 0) return;
    setDialogText?.(t('RoleList.DeletingDialog'));
    setVisible?.(true);
    Promise.allSettled(selected.map((el) => deleteRequest(`/api/roles/${el.id}`)))
      .then((results) => {
        setVisible?.(false);
        const ok = results.filter((r) => r.status === 'fulfilled').length;
        const fail = results.filter((r) => r.status === 'rejected').length;
        if (ok > 0) refreshList();
        if (ok > 0 && fail === 0) {
          toast?.current?.show({
            severity: 'info',
            summary: t('ServiceTask.Confirmed'),
            life: 3000,
            detail: ok === 1 ? t('RoleList.Toast.OneRoleDeleted') : t('RoleList.Toast.NRolesDeleted').replace('{{n}}', String(ok)),
          });
        } else if (fail > 0) {
          toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.Rejected'), detail: t('RoleList.Toast.SomeDeletesFailed'), life: 3000 });
        }
      });
  }, [getTable, refreshList, setDialogText, setVisible, t, toast]);

  const confirmDelete = () => {
    confirmDialog({
      message: t('RoleList.ConfirmDeleteMany'),
      header: t('ServiceTask.ConfirmDeleteTitle'),
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('Common.Yes'),
      rejectLabel: t('Common.No'),
      accept: deleteSelected,
      reject: () => toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.Rejected'), detail: t('ServiceTask.RequestCancelled'), life: 3000 }),
    });
  };

  function addNewRole() {
    dispatch({ type: 'create_new_tab', name: t('RoleList.Tab.New'), translationKey: 'RoleList.Tab.New', legacyLabel: t('RoleList.Tab.New'), page: 'RoleAdd', props: { role: null, type: 'add' }, mask: 'gset012' });
  }

  function copyRole() {
    const sel = getTable()?.getSelectedData?.()?.[0];
    if (sel) {
      dispatch({ type: 'create_new_tab', name: t('RoleList.Tab.New'), translationKey: 'RoleList.Tab.New', legacyLabel: t('RoleList.Tab.New'), page: 'RoleAdd', props: { role: sel, type: 'add' }, mask: 'gset012' });
    }
  }

  function editRole() {
    const sel = getTable()?.getSelectedData?.()?.[0];
    if (sel) onEditRow(sel);
  }

  const searchTable = () => {
    const normalized = String(searchValue ?? '').trim();
    submittedSearchValueRef.current = normalized;
    setSearchIconColor(normalized === '' ? '' : 'active');
    const table = getTable();
    if (!table) return;
    if (normalized === '') table.clearFilter(true);
    else {
      const needle = normalizeText(normalized);
      table.setFilter((data) => normalizeText(JSON.stringify(data)).includes(needle));
    }
    setVisibleSearch(false);
  };

  const scrollToFirst = () => scrollToFirstUtil(getTable());
  const scrollUp = () => scrollUpUtil(getTable());
  const scrollDown = () => scrollDownUtil(getTable());
  const scrollToLast = () => scrollToLastUtil(getTable());

  const itemRenderer = (item, options) => (
    <button type="button" className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px] border-0 text-left" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
    </button>
  );

  const items = useMemo(() => [{
    key: '0',
    label: t('Common.Actions'),
    icon: 'pi pi-sliders-v',
    template: itemRenderer,
    items: [
      { key: '0_1', label: t('Common.SetFilter'), icon: 'pi pi-filter', command: () => { setUserMaskFilter('RoleList', 'set_filters', null, getTable()?.getHeaderFilters?.() ?? [], null); closeMenu(); }, template: itemRenderer },
      { key: '0_2', label: t('Common.ClearFilter'), icon: 'pi pi-filter-slash', command: () => { setUserMaskFilter('RoleList', 'set_filters', null, [], null); getTable()?.clearHeaderFilter?.(); closeMenu(); }, template: itemRenderer },
      { key: '0_3', label: t('Common.SetSorting'), icon: 'pi pi-sort-alt', command: () => { setUserMaskFilter('RoleList', 'set_sorters', null, null, null, cleanSorters(getTable()?.getSorters?.() ?? [])); closeMenu(); }, template: itemRenderer },
      { key: '0_4', label: t('Common.ClearSorting'), icon: 'pi pi-sort-alt-slash', command: () => { setUserMaskFilter('RoleList', 'set_sorters', null, null, null, []); getTable()?.setSort?.([]); closeMenu(); }, template: itemRenderer },
      { key: '0_5', label: t('Common.SetMask'), icon: 'pi pi-server', command: openMaskDialog, template: itemRenderer },
      ...(hasApplicableStandardMask ? [{ key: '0_5s', label: t('Common.StandardMask'), icon: 'pi pi-clone', command: confirmApplyStandardLayoutToUserMask, template: itemRenderer }] : []),
      { key: '0_6', label: t('ServiceTask.Notifications'), icon: 'pi pi-envelope', command: () => { openChosenNotificationPreferences('RoleList'); closeMenu(); }, template: itemRenderer },
    ],
  }], [closeMenu, confirmApplyStandardLayoutToUserMask, getTable, hasApplicableStandardMask, openChosenNotificationPreferences, openMaskDialog, setUserMaskFilter, t]);

  const handleClickOutside = (event) => {
    if (overlayRef.current && !overlayRef.current.contains(event.target)) setExpandedKeys({});
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  const list = state.roles || [];

  return (
    <>
      <div className="w-full h-10 flex flex-row bg-[#333]">
        <IntersectionObserverWrapper>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="add">
            <button type="button" className="disabled:opacity-25" disabled={insertIcon} onClick={addNewRole}>
              <AddIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="edit">
            <button type="button" className="disabled:opacity-25" disabled={editIcon} onClick={editRole}>
              <EditIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="copy">
            <button type="button" className="disabled:opacity-25" disabled={copyIcon} onClick={copyRole}>
              <CopyIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="delete">
            <button type="button" className="disabled:opacity-25" disabled={deleteIcon} onClick={confirmDelete}>
              <DeleteIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="print">
            <button type="button" onClick={() => setPrintChoiceVisible(true)}>
              <PrintIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="search">
            <button type="button" title={searchIconColor !== 'active' ? t('Common.Search') : t('ServiceTask.CancelSearch')} onClick={searchIconColor !== 'active' ? () => setVisibleSearch(true) : () => { setSearchIconColor(''); setSearchValue(''); submittedSearchValueRef.current = ''; getTable()?.clearFilter?.(true); }}>
              {searchIconColor === '' ? <MdSearch fill="#fff" className="w-[28px] h-[28px]" aria-hidden="true" /> : <MdSearchOff fill="#fff" className="w-[28px] h-[28px]" aria-hidden="true" />}
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="export">
            <button type="button" onClick={exportRoleXlsx} title={t('Common.ExportToExcel')}><ExportIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="refresh">
            <button type="button" onClick={() => refreshList(true)} title={t('Common.Refresh')}>
              <RefreshIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="first">
            <button type="button" onClick={scrollToFirst} title={t('Icon.First')}><BackToFirstIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="back">
            <button type="button" onClick={scrollUp} title={t('Icon.Previous')}><BackIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="forward">
            <button type="button" onClick={scrollDown} title={t('Icon.Next')}><ForwardIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="last">
            <button type="button" onClick={scrollToLast} title={t('Icon.Last')}><ForwardToLastIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="favorite">
            <button type="button" onClick={() => openFavoriteDialog(t('RoleList'), 'RoleList')}>
              <FavoriteIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="price">
            <button type="button" onClick={() => setFastView('RoleList', columnsVisible, columns)}>
              <PriceIcon />
            </button>
          </div>
          {canSetStandardMask ? (
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="save-standard-mask">
              <button type="button" title={t('Common.SaveStandardMask')} aria-label={t('Common.SaveStandardMask')} onClick={confirmSaveStandardMask} className="flex items-center justify-center">
                <i className="pi pi-bookmark text-white text-xl" aria-hidden="true" />
              </button>
            </div>
          ) : null}
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          <div className="card flex justify-content-center z-[100]" data-targetid="actions" ref={overlayRef}>
            <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
          </div>
        </IntersectionObserverWrapper>
      </div>

      <div className="px-1 bg-white rounded-md shadow-md">
        <div className="flex flex-col overflow-x-auto no-scrollbar min-h-[calc(100vh-177px)]">
          <div className="overflow-hidden">
            <div className="inline-block w-full py-2">
              <ReactTabulator
                key="role-list"
                data={list}
                className="h-[calc(100vh_-_193px)]"
                onRef={(r) => { ref.current = r?.current; }}
                columns={columns}
                events={{
                  rowSelectionChanged: onSelectRow,
                  tableBuilt: () => {
                    const table = getTable();
                    if (!table) return;
                    const effective = getEffectiveMask('RoleList', state);
                    if (effective?.type === 'user' && effective.userMask) {
                      const m = effective.userMask;
                      isRestoringRef.current = true;
                      applyUserMaskRowToTabulator(table, m, {
                        setColumnsVisible: (v) => setColumnsVisible(mergeRoleVisibility(v)),
                        clearHeaderFiltersWhenEmpty: true,
                        applyColumnVisibilityFn: (tbl, jc, setVis) => {
                          const merged = mergeRoleVisibility(jc);
                          setVis(merged);
                          applyDataColumnVisibilityToTableRole(tbl, merged);
                        },
                      });
                      {
                        const merged = mergeRoleVisibility(safeParse(m.columns, {}));
                        for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_ROLE)) {
                          if (merged[field] === false) clearHeaderFilterForField(table, field);
                        }
                      }
                      setTimeout(() => { isRestoringRef.current = false; }, 0);
                    } else if (effective?.type === 'standard' && effective.standardMask) {
                      const m = effective.standardMask;
                      isRestoringRef.current = true;
                      const attributes = sanitizeTabulatorLayoutAttributes(safeParse(m.attributes, []));
                      const jsonColumns = safeParse(m.columns, {});
                      if (Array.isArray(attributes) && attributes.length > 1) table.setColumnLayout?.(attributes);
                      if (jsonColumns && typeof jsonColumns === 'object') {
                        const merged = mergeRoleVisibility(jsonColumns);
                        setColumnsVisible(merged);
                        applyDataColumnVisibilityToTableRole(table, merged);
                      }
                      setTimeout(() => { isRestoringRef.current = false; }, 0);
                    } else {
                      applyDataColumnVisibilityToTableRole(table, DEFAULT_COLUMNS_VISIBLE_ROLE);
                    }
                  },
                  ...getTabulatorPersistLayoutEvents('RoleList', ref, setUserMaskFilter),
                }}
                options={{
                  layout: 'fitColumns',
                  renderHorizontal: 'virtual',
                  movableColumns: true,
                  autoResize: true,
                  headerSortTristate: true,
                  filterMode: 'local',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <PrintOptionsDialog
        visible={printChoiceVisible}
        onHide={() => setPrintChoiceVisible(false)}
        orientation={printOrientationChoice}
        onOrientationChange={setPrintOrientationChoice}
        layout={printLayoutChoice}
        onLayoutChange={setPrintLayoutChoice}
        onConfirm={runRolePrint}
      />

      <Dialog header={t('DepartmentList.SearchDialogTitle')} visible={visibleSearch} style={{ width: '40vw' }} onHide={() => setVisibleSearch(false)}>
        <div className="flex flex-col">
          <p className="text-sm text-gray-600 mb-2">{t('DepartmentList.SearchHint')}</p>
          <div className="flex items-center gap-3 w-full">
            <input value={searchValue} onChange={(e) => setSearchValue(e.target.value)} type="text" className="flex-1 h-10 px-4 border rounded-md focus:border-gray-400" />
            <button type="button" onClick={searchTable} className="h-10 w-16 bg-zinc-900 text-white rounded-md hover:bg-zinc-800 flex items-center justify-center">
              <SearchIcon />
            </button>
          </div>
        </div>
      </Dialog>

      <TabulatorMaskDialog
        visible={maskVisible}
        onHide={() => setMaskVisible(false)}
        title={t('RoleList.MaskDialogTitle')}
        fields={Object.keys(DEFAULT_COLUMNS_VISIBLE_ROLE)}
        checkedMap={columnsVisible}
        onToggleField={setMask}
        onSelectAll={maskSelectAll}
        onDeselectAll={maskDeselectAll}
        onSave={saveRoleMaskColumns}
        selectAllLabel={t('RoleList.MaskSelectAll')}
        deselectAllLabel={t('RoleList.MaskDeselectAll')}
        saveLabel={t('RoleList.MaskSave')}
        getFieldLabel={getMaskColumnLabel}
        inputIdPrefix="role-mask"
      />
    </>
  );
}
